// Implements: F-UIX-019, F-RPT-006
/**
 * Mockup v3 P9 (default P4-1): the client of `GET /reports/platform/latency?organization_id=&from=&to=`,
 * one organization's latency and errors per model and step, for the platform admin only (anybody
 * else gets the `404` of a path that does not exist). An answer with no body is broken.
 *
 * Its export twin (`…/latency/export`, owner 2026-10-19) takes the same organization and window
 * plus `format=csv`: the same access first, and a `422` over the server's row cap that names no file.
 */

import { API_BASE } from "../../../shared/api/apiBase";
import { httpDownload, httpRequest } from "../../../shared/api/httpClient";
import type { Download } from "../../../shared/api/httpClient";
import { reportWindowQuery } from "../../../shared/api/reportWindow";
import type { LatencyReportView, ReportWindow } from "../../../types/reports";

const LATENCY_PATH = `${API_BASE}/reports/platform/latency`;

function latencyQuery(organizationId: string, window: ReportWindow): URLSearchParams {
  const query = new URLSearchParams({ organization_id: organizationId });
  for (const [key, value] of reportWindowQuery(window)) query.set(key, value);
  return query;
}

export async function fetchPlatformLatency(
  organizationId: string,
  window: ReportWindow,
  signal?: AbortSignal,
): Promise<LatencyReportView> {
  const path = `${LATENCY_PATH}?${latencyQuery(organizationId, window).toString()}`;
  const body = await httpRequest<LatencyReportView>(path, { signal });
  if (body === undefined) throw new Error(`empty answer from ${path}`);
  return body;
}

/** The file name of an export whose answer names none: the server's own stem (`export_name`). */
export const PLATFORM_LATENCY_EXPORT_NAME = "latencia-organizacion.csv";

export function platformLatencyExportPath(organizationId: string, window: ReportWindow): string {
  const query = latencyQuery(organizationId, window);
  query.set("format", "csv");
  return `${LATENCY_PATH}/export?${query.toString()}`;
}

export function downloadPlatformLatency(organizationId: string, window: ReportWindow): Promise<Download> {
  return httpDownload(platformLatencyExportPath(organizationId, window));
}
